import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { SchoolSidebar } from "../components/ui/school/school-dashboard/school-sidebar"
import DashboardHeader from "../components/ui/school/school-dashboard/dashboard-header"
import { StatCardsRow } from "../components/ui/school/school-dashboard/stats-cards"
import { CareerPathChart } from "../components/ui/school/school-dashboard/career-path-chart"
import { ClassPerformanceTable } from "../components/ui/school/school-dashboard/class-performance-table"
import { schoolStats, careerPathDistribution, classPerformance } from "../lib/school-dashboard-data"
import { Target, TrendingUp, AlertCircle } from "lucide-react"
import ProtectedRoute from "../components/ProtectedRoute"
import { AuthStore } from "../hooks/stores/AuthStore"
import { useUserData } from "../hooks/useUserData"

const SchoolDashboardPage = () => {
  const navigate = useNavigate()
  const {isLoggedIn, currentUser,  _hasHydrated} = AuthStore()
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [user, setUser] = useState(null)

  useEffect(() => {
    // const userData = localStorage.getItem("user")
    if (_hasHydrated) {
      if (!isLoggedIn || currentUser?.role != "school")
      navigate("/signin")
    }

    // if{!isLoading}

    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [navigate, isLoggedIn, currentUser,  _hasHydrated])

  if (!_hasHydrated || !isLoggedIn) return null
  

  return (
   <ProtectedRoute>
     <div className="flex min-h-screen bg-gray-50">
      <SchoolSidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} currentPage="dashboard" />

      <main
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarOpen && !isMobile ? "md:ml-64" : ""}`}
      >
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          {/* Header Section */}
          <DashboardHeader />
          
          {/* Quick Stats */}
          <StatCardsRow stats={schoolStats} />

          {/* Main Dashboard Content - 65%/35% Split */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Left Column - Career Path Chart (65%) */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="w-4 h-4 text-blue-600" />
                      <h2 className="text-lg font-bold text-gray-900">Career Path Analysis</h2>
                    </div>
                    <p className="text-sm text-gray-500">Distribution of students across different career paths</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600">+12% growth</span>
                  </div>
                </div>
                <CareerPathChart data={careerPathDistribution} />
              </div>
            </div>

            {/* Right Column - Class Performance (35%) */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 h-full">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                        <TrendingUp className="w-4 h-4 text-purple-600" />
                      </div>
                      <h2 className="text-lg font-bold text-gray-900">Class Performance</h2>
                    </div>
                    <p className="text-sm text-gray-500">Top performing classes this month</p>
                  </div>
                   </div>
                <ClassPerformanceTable classes={classPerformance.slice(0, 3)} />
                
                {/* Performance Summary */}
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Average Completion Rate</p>
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full w-3/4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                        </div>
                        <span className="text-sm font-bold text-gray-900">76%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <AlertCircle className="w-3 h-3" />
                      <span>3 classes need attention</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Metrics Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Top Career Paths */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Career Paths</h3>
              <div className="space-y-3">
                {careerPathDistribution.slice(0, 3).map((path, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        index === 0 ? 'bg-blue-100' : 
                        index === 1 ? 'bg-purple-100' : 'bg-green-100'
                      }`}>
                        <span className={`text-sm font-bold ${
                          index === 0 ? 'text-blue-600' : 
                          index === 1 ? 'text-purple-600' : 'text-green-600'
                        }`}>
                          #{index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{path.name}</p>
                        <p className="text-xs text-gray-500">{path.value} students</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">
                        {Math.round((path.value / careerPathDistribution.reduce((sum, p) => sum + p.value, 0)) * 100)}%
                      </p>
                      <p className="text-xs text-gray-500">share</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Activity</h3>
              <div className="space-y-3">
                {[
                  { action: 'Career assessment completed', user: 'Sarah Johnson', time: '2 hours ago' },
                  { action: 'New class added', user: 'Grade 11 Science', time: '5 hours ago' },
                  { action: 'Progress report generated', user: 'Mathematics Dept.', time: '1 day ago' },
                ].map((activity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500"></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{activity.action}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500">{activity.user}</p>
                        <p className="text-xs text-gray-400">{activity.time}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Key Metrics</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Student Engagement</span>
                    <span className="text-sm font-bold text-gray-900">84%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full w-4/5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Task Completion</span>
                    <span className="text-sm font-bold text-gray-900">76%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full w-3/4 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Career Clarity</span>
                    <span className="text-sm font-bold text-gray-900">68%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full w-2/3 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
   </ProtectedRoute>
  )
}

export default SchoolDashboardPage